import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, PageHeader, Spinner, Button, Tabs, EmptyState, formatCents } from '../components/ui';

type ReportTab = 'revenue' | 'bookings' | 'contacts' | 'campaigns' | 'pipelines' | 'attribution';

export function ReportsPage() {
  const [active, setActive] = useState<ReportTab>('revenue');

  return (
    <div>
      <PageHeader title="Reports" subtitle="Understand what's working" />
      <Tabs<ReportTab>
        active={active}
        onChange={setActive}
        tabs={[
          { value: 'revenue', label: 'Revenue' },
          { value: 'bookings', label: 'Bookings' },
          { value: 'contacts', label: 'Contacts' },
          { value: 'campaigns', label: 'Campaigns' },
          { value: 'pipelines', label: 'Pipelines' },
          { value: 'attribution', label: 'Attribution' },
        ]}
      />
      {active === 'revenue' && <RevenueReport />}
      {active === 'bookings' && <SimpleReport title="Bookings" path="/reports/bookings" />}
      {active === 'contacts' && <SimpleReport title="Contacts" path="/reports/contacts" />}
      {active === 'campaigns' && <SimpleReport title="Campaigns" path="/reports/campaigns" />}
      {active === 'pipelines' && <SimpleReport title="Pipelines" path="/reports/pipelines" />}
      {active === 'attribution' && <SimpleReport title="Attribution" path="/reports/attribution" />}
    </div>
  );
}

function RevenueReport() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);
  const from = thirtyDaysAgo.toISOString().slice(0, 10);
  const to = today.toISOString().slice(0, 10);

  const q = useQuery({
    queryKey: ['reports-revenue', from, to],
    queryFn: () =>
      api.get<{
        totalCents: number;
        paidCents: number;
        outstandingCents: number;
        series: { date: string; cents: number }[];
      }>(
        '/reports/revenue',
        { from, to, groupBy: 'day' }
      ).catch(() => ({ totalCents: 0, paidCents: 0, outstandingCents: 0, series: [] })),
  });

  if (q.isLoading) return <Spinner />;
  const data = q.data!;

  const max = Math.max(...data.series.map((s) => s.cents), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)' }}>
        <Card>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Total</div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{formatCents(data.totalCents)}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Paid</div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-feedback-success)' }}>
            {formatCents(data.paidCents)}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Outstanding</div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-feedback-warning)' }}>
            {formatCents(data.outstandingCents)}
          </div>
        </Card>
      </div>

      <Card>
        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Last 30 days</h3>
        {data.series.length === 0 ? (
          <EmptyState title="No data yet" description="Revenue shows up here once you record payments." />
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 200 }}>
            {data.series.map((s) => (
              <div
                key={s.date}
                title={`${s.date}: ${formatCents(s.cents)}`}
                style={{
                  flex: 1,
                  background: 'var(--color-brand-primary)',
                  height: `${(s.cents / max) * 100}%`,
                  minHeight: 2,
                  borderRadius: 'var(--radius-sm)',
                }}
              />
            ))}
          </div>
        )}
      </Card>

      <Button
        variant="secondary"
        onClick={() => {
          const url = `${(import.meta.env.VITE_API_URL as string | undefined) ?? 'https://frontdesk-api.blancorunning.workers.dev'}/api/v1/reports/revenue/export?from=${from}&to=${to}`;
          window.open(url, '_blank');
        }}
      >
        Export CSV
      </Button>
    </div>
  );
}

function SimpleReport({ title, path }: { title: string; path: string }) {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);
  const from = thirtyDaysAgo.toISOString().slice(0, 10);
  const to = today.toISOString().slice(0, 10);

  const q = useQuery({
    queryKey: ['report', path, from, to],
    queryFn: () => api.get<Record<string, unknown>>(path, { from, to }).catch(() => ({})),
  });

  if (q.isLoading) return <Spinner />;
  const data = q.data ?? {};

  return (
    <Card>
      <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 600 }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
        {Object.entries(data).map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {formatLabel(k)}
            </div>
            <div style={{ fontSize: 'var(--text-xl)', fontWeight: 600, marginTop: 4 }}>{renderValue(v)}</div>
          </div>
        ))}
      </div>
      {Object.keys(data).length === 0 && (
        <EmptyState title={`No ${title.toLowerCase()} data yet`} description="Once the system has activity, reports will populate." />
      )}
    </Card>
  );
}

function formatLabel(k: string) {
  return k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (m) => m.toUpperCase());
}

function renderValue(v: unknown): string {
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return `${v.length}`;
  return JSON.stringify(v);
}
